import { useCallback, useEffect, useState } from "react";
import { Alert, Avatar, Box, Button, ButtonBase, Card, CardContent, Grid, IconButton, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import AirlineSeatReclineNormal from "@mui/icons-material/AirlineSeatReclineNormal";
import ConfirmationNumberOutlined from "@mui/icons-material/ConfirmationNumberOutlined";
import EscalatorWarningOutlined from "@mui/icons-material/EscalatorWarningOutlined";
import DirectionsBusOutlined from "@mui/icons-material/DirectionsBusOutlined";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";
import PeopleAltOutlined from "@mui/icons-material/PeopleAltOutlined";
import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";

import { post } from "../services/api";
import { useNavigate } from "react-router-dom";
import ENDPOINTS from "../utils/constants/endpoints";
import DashboardCard, { DASHBOARD_COLORS as C, focusRing, hasCount, STATUS_COLORS, statusDot } from "../components/DashboardCard";
import { useSelector } from "react-redux";
import { cardStyle, contentStyle, pageStyle } from "../components/busDetails/detailLayout";

const SCHOOL_ROLE = "Nw==";
// Dashboard-only overrides; the shared detail layout stays as Bus/Driver views use it.
const dashboardPage = { ...pageStyle, bgcolor: C.page, color: C.navy };
const dashboardCard = { ...cardStyle, borderColor: C.border, borderRadius: "14px" };
const sectionContent = { ...contentStyle, p: 2, "&:last-child": { pb: 2 } };
const overviewContent = { ...contentStyle, p: 1.75, "&:last-child": { pb: 1.75 } };
// Three columns when there is room, two on tablets, stacked on phones.
const overviewGrid = { display: "grid", gap: 1.25, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))" } };

function SectionTitle({ children }) {
  return <Typography component="h2" sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.3, color: C.navy, mb: 1 }}>{children}</Typography>;
}

function VerificationSummary({ title, total, pending, approved, rejected }) {
  const statuses = { pending, approved, rejected };
  return (
    <Box component="article" aria-label={`${title} verification`} sx={{ p: 1.5, borderRadius: "10px", border: `1px solid ${C.border}`, bgcolor: "#FFFFFF", minWidth: 0 }}>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, color: C.navy }}>{title}</Typography>
      <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mt: 0.5, mb: 1 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1.2, color: C.navy, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{total ?? '—'}</Typography>
        <Typography sx={{ fontSize: 12, color: C.muted }}>Total</Typography>
      </Stack>
      <Box component="dl" sx={{ m: 0, display: "grid", gap: 0.75 }}>
        {Object.entries(statuses).map(([key, value]) => (
          <Box key={key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Box component="dt" sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12, color: C.muted }}>
              <Box component="span" aria-hidden="true" sx={{ ...statusDot(STATUS_COLORS[key].color), width: 6, height: 6, flexShrink: 0 }} />
              {STATUS_COLORS[key].label}
            </Box>
            <Box component="dd" sx={{ m: 0, fontSize: 12, fontWeight: 700, color: C.navy, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere", minWidth: 0 }}>{value ?? '—'}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function QuickLink({ Icon, label, hint, onClick }) {
  return (
    <ButtonBase onClick={onClick}
      sx={{
        width: "100%", justifyContent: "flex-start", alignItems: "center", textAlign: "left", gap: 1.5, px: 1.25, py: 1, borderRadius: "10px",
        border: `1px solid ${C.border}`, bgcolor: "#fff", transition: "border-color .2s ease, background-color .2s ease, box-shadow .2s ease",
        "& .quick-arrow": { color: "#94A3B8", transition: "color .2s ease, transform .2s ease" },
        "&:hover": { borderColor: "#BFDBFE", bgcolor: "#F8FAFF", boxShadow: "0 2px 8px rgba(37,99,235,.08)" },
        "&:hover .quick-arrow": { color: C.primary, transform: "translateX(2px)" },
        ...focusRing,
      }}>
      <Avatar variant="rounded" sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: C.primarySoft, color: C.primary }}><Icon fontSize="small" /></Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3, color: C.navy }}>{label}</Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1.35, color: C.muted }}>{hint}</Typography>
      </Box>
      <ChevronRightRounded className="quick-arrow" fontSize="small" />
    </ButtonBase>
  );
}

const HomePage = () => {
  const [info, setInfo] = useState({});
  const [status, setStatus] = useState("loading");
  const navigate = useNavigate();
  const { role_id, user } = useSelector((state) => state.auth);
  const isSchool = role_id === SCHOOL_ROLE;

  const dashboardApi = useCallback(async () => {
    setStatus("loading");
    try {
      const token = localStorage.getItem("token");
      const payload = { token, format: "Json" };
      const result = await post(ENDPOINTS.DASHBOARD.INFO, payload);
      if ((result?.status && result.status !== 'success') || !result?.data?.dbmaster || typeof result.data.dbmaster !== 'object' || Array.isArray(result.data.dbmaster)) throw new Error('Invalid dashboard response');
      setInfo(result?.data?.dbmaster || {});
      setStatus("ready");
    } catch (err) {
      console.error("Dashboard error:", err);
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    dashboardApi();
  }, [dashboardApi]);

  const counts = (prefix) => ({
    total: info?.[`${prefix}_total`],
    pending: info?.[`${prefix}_pending`],
    approved: info?.[`${prefix}_approve`],
    rejected: info?.[`${prefix}_reject`],
  });

  // Same rules as before: School card hidden for school users; Add buttons only for school users.
  const cardData = [
    !isSchool && { key: "school", title: "Schools", Icon: SchoolOutlined, ...counts("school") },
    { key: "driver", title: "Drivers", Icon: AirlineSeatReclineNormal, addLabel: "Add Driver", addPath: "/add-driver", ...counts("driver") },
    { key: "conductor", title: "Conductors", Icon: ConfirmationNumberOutlined, addLabel: "Add Conductor", addPath: "/add-driver", ...counts("conductor") },
    { key: "attendant", title: "Attendants", Icon: EscalatorWarningOutlined, addLabel: "Add Attendant", addPath: "/add-driver", ...counts("attendant") },
    { key: "bus", title: "Buses", Icon: DirectionsBusOutlined, addLabel: "Add Bus", addPath: "/buses/add", ...counts("bus") },
  ].filter(Boolean);

  const people = cardData.filter((card) => ["driver", "conductor", "attendant"].includes(card.key));
  const showOverview = people.some((card) => hasCount(card.pending) || hasCount(card.approved) || hasCount(card.rejected));

  // Shortcuts mirror the sidebar's role rules; Add actions already live on the cards.
  const quickLinks = [
    { Icon: PeopleAltOutlined, label: "Driver/Conductor List", hint: "Drivers, conductors and attendants", path: "/drivers" },
    isSchool && { Icon: DirectionsBusOutlined, label: "Bus Management", hint: "Buses and certificates", path: "/buses" },
    !isSchool && { Icon: SchoolOutlined, label: "Schools", hint: "Registered schools", path: "/schools" },
    { Icon: AccountCircleOutlined, label: "My Profile", hint: "Account details", path: "/profile" },
  ].filter(Boolean);

  const gridColumns = { display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: `repeat(${Math.min(cardData.length, 5)}, minmax(0, 1fr))` } };

  return (
    <Box sx={dashboardPage}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h1" fontWeight={700} sx={{ fontSize: { xs: 20, sm: 23 }, lineHeight: 1.2, color: C.navy, overflowWrap: "anywhere" }}>
            {user?.name ? user.name.toUpperCase() : "Dashboard"}
          </Typography>
          {user?.name && <Typography sx={{ fontSize: 13, color: C.muted }}>Dashboard overview</Typography>}
        </Box>
        <Tooltip title="Profile">
          <IconButton onClick={() => navigate("/profile")} aria-label="Open profile" sx={{ p: 0.5, border: `1px solid ${C.border}`, bgcolor: "#fff", "&:hover": { bgcolor: C.primarySoft }, ...focusRing }}>
            <Avatar src="/profile.png" sx={{ width: 32, height: 32 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {status === "error" ? (
        <Alert severity="error" action={<Button onClick={dashboardApi}>Retry</Button>}>Unable to load dashboard counts. Check your connection and try again.</Alert>
      ) : status === "loading" ? (
        <Box role="status" aria-label="Loading dashboard" sx={gridColumns}>
          {cardData.map((card) => <Skeleton key={card.key} variant="rounded" height={210} sx={{ borderRadius: "14px" }} />)}
        </Box>
      ) : (
        <>
          <Box component="section" aria-label="Overview" sx={gridColumns}>
            {cardData.map(({ key, title, Icon, addLabel, addPath, total, pending, approved, rejected }) => (
              <DashboardCard key={key} title={title} Icon={Icon} total={total} pending={pending} approved={approved} rejected={rejected}
                onOpen={key !== "school" ? () => navigate(key === 'bus' ? '/buses' : '/drivers') : undefined}
                addLabel={addLabel} onAdd={isSchool && addPath ? () => navigate(addPath) : undefined} />
            ))}
          </Box>

          <Grid container spacing={2} mt={2} alignItems="flex-start">
            {!showOverview && (
              <Grid size={{ xs: 12, md: 7, lg: 8 }}>
                <Card sx={{ ...dashboardCard, height: "auto" }}>
                  <CardContent sx={overviewContent}>
                    <SectionTitle>Verification overview</SectionTitle>
                    <Box sx={overviewGrid}>
                      {people.map(({ key, ...card }) => <VerificationSummary key={key} {...card} />)}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
            <Grid size={{ xs: 12, md: showOverview ? 5 : 12, lg: showOverview ? 4 : 12 }}>
              <Card sx={dashboardCard}>
                <CardContent sx={sectionContent}>
                  <SectionTitle>Quick links</SectionTitle>
                  <Box sx={{ display: "grid", gap: 1, gridTemplateColumns: showOverview ? "minmax(0, 1fr)" : { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" } }}>
                    {quickLinks.map((link) => <QuickLink key={link.path} {...link} onClick={() => navigate(link.path)} />)}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default HomePage;
